import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DialogComponentService } from '@merchant-portal/app/components/dialog/dialog.component.service';
import { UtilService } from '@merchant-portal/app/services/util.service';
import { ComponentStore } from '@ngrx/component-store';
import { MessageService } from 'primeng/api';
import { EMPTY, interval, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, switchMap, takeWhile, tap } from 'rxjs/operators';
import { AuthService } from '../../auth.service';
import { OTP_EVENT_TYPE } from '../constant/event.type';
import { OtpParams, OtpState, OtpStoreFactory } from '../factory/otp-store.factory';
import { OtpApiService } from '../services/otp.api.service';

const initialState: OtpState = {
  errorMessage: '',
  isLoading: false,
  countdown: 30,
  isCountdownActive: true,
  isAccountBlocked: false,
  isSuspendAccount: false,
  isEventFromInvitation: false,
  isVeritifyOtpSuccess: false,
  otpToken: '',
  resendDelaySeconds: 30,
};

@Injectable()
export class OtpStore extends ComponentStore<OtpState> implements OtpStoreFactory, OnDestroy {
  private countdownSubscription?: Subscription;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private otpApiService: OtpApiService,
    private authService: AuthService,
    private dialogComponentService: DialogComponentService,
    private router: Router,
    private utilService: UtilService,
    private messageService: MessageService
  ) {
    super(initialState);
    this.select(state => state.isAccountBlocked)
      .pipe(filter(isBlocked => isBlocked))
      .subscribe(() => this.showBlockedDialog());
  }

  errorMessage$ = this.select(state => state.errorMessage);
  isLoading$ = this.select(state => state.isLoading);
  countdown$ = this.select(state => state.countdown);
  isCountdownActive$ = this.select(state => state.isCountdownActive);
  isAccountBlocked$ = this.select(state => state.isAccountBlocked);
  isSuspendAccount$ = this.select(state => state.isSuspendAccount);
  isEventFromInvitation$ = this.select(state => state.isEventFromInvitation);
  resendDelaySeconds$ = this.select(state => state.resendDelaySeconds);

  vm$ = this.select({
    errorMessage: this.errorMessage$,
    isLoading: this.isLoading$,
    countdown: this.countdown$,
    isCountdownActive: this.isCountdownActive$,
    isAccountBlocked: this.isAccountBlocked$,
    isSuspendAccount: this.isSuspendAccount$,
    isEventFromInvitation: this.isEventFromInvitation$,
    isVeritifyOtpSuccess: this.select(state => state.isVeritifyOtpSuccess),
    otpToken: this.select(state => state.otpToken),
    resendDelaySeconds: this.resendDelaySeconds$,
  });

  verifyOtp = this.effect<OtpParams>(otpParams$ =>
    otpParams$.pipe(
      tap(otp => {
        if (otp.otp.length < 6) {
          this.patchState({
            errorMessage: '',
            isAccountBlocked: false,
            isVeritifyOtpSuccess: false,
          });
        }
      }),
      filter(otp => otp.otp.length === 6),
      tap(() => this.patchState({ isLoading: true })),
      switchMap(otp =>
        this.otpApiService.verifyOtp(otp.otp, this.authService.getTempToken() ?? '').pipe(
          switchMap(res => {
            this.patchState({
              errorMessage: '',
              isVeritifyOtpSuccess: true,
              isLoading: false,
            });
            this.clearOtpSession(); // Clear session on successful verification
            this.authService.setTempToken(res.data.token);
            if (otp.event === 'first-time-login') {
              this.patchState({ isEventFromInvitation: true });
              return EMPTY;
            } else if (otp.event === OTP_EVENT_TYPE.CHANGE_PASSWORD) {
              this.patchState({ isEventFromInvitation: true });
              return EMPTY;
            } else if (otp.event === OTP_EVENT_TYPE.FORGOT_PASSWORD) {
              return EMPTY;
            } else if (otp.event === OTP_EVENT_TYPE.USER_LOGIN) {
              return this.authService.getIpInfo().pipe(
                tap(() => this.patchState({ isLoading: true })),
                catchError(() => {
                  // If getIpInfo fails, continue with empty/default IP info
                  return of({ ip: '', country: '', city: '' });
                }),
                switchMap(ipInfo => {
                  return this.otpApiService
                    .successVerify(
                      this.authService.getTempToken() || '',
                      ipInfo,
                      this.authService.getIsRemember() || '',
                      this.authService.getFingerPrintId(this.authService.getEmail() || '') || ''
                    )
                    .pipe(
                      tap(res => {
                        this.patchState({ isLoading: false });
                        this.authService.setUserInfo(res.data.userInfo);
                        this.authService.setAccessToken(res.data.accessToken);
                        this.authService.setRefreshToken(res.data.refreshToken);
                        this.checkAccessToken();
                      }),
                      catchError(({ error }: HttpErrorResponse) => {
                        this.patchState({
                          isLoading: false,
                          errorMessage: error.errors,
                        });
                        return EMPTY;
                      })
                    );
                })
              );
            }
            return EMPTY;
          }),
          catchError(({ error }: HttpErrorResponse) => {
            this.patchState({ isLoading: false });
            if (error.errors.includes('blocked')) {
              this.patchState({ isAccountBlocked: true });
            } else if (error.errors.includes('expired')) {
              this.patchState({ errorMessage: 'OTP Expired' });
            } else {
              this.patchState({ errorMessage: error.errors });
            }
            return EMPTY;
          })
        )
      )
    )
  );

  resendOtp = this.effect<{ event: string; twoFactorAuthMethod?: string }>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.patchState({ isLoading: true });
      }),
      switchMap(params =>
        this.otpApiService
          .resendOtp({
            email: this.authService.getEmail() || '',
            event: params.event,
            twoFactorAuthMethod: params.twoFactorAuthMethod,
          })
          .pipe(
            tap(res => {
              this.authService.setTempToken(res.data.token);
              // Use resendDelaySeconds from API response for login, change password, forgot password, reset pin, and first-time login events, fallback to current or default for others
              const shouldUseApiDelaySeconds = [
                OTP_EVENT_TYPE.USER_LOGIN,
                OTP_EVENT_TYPE.CHANGE_PASSWORD,
                OTP_EVENT_TYPE.FORGOT_PASSWORD,
                OTP_EVENT_TYPE.FORGOT_PIN,
                OTP_EVENT_TYPE.FIRST_TIME_LOGIN,
              ].includes(params.event as OTP_EVENT_TYPE);

              const newDelaySeconds =
                shouldUseApiDelaySeconds && res.data.resendDelaySeconds
                  ? res.data.resendDelaySeconds
                  : this.get().resendDelaySeconds || 30;

              this.patchState({
                errorMessage: '',
                isLoading: false,
                otpToken: res.data.token,
                resendDelaySeconds: newDelaySeconds,
              });
              this.startCountdown();
            }),
            catchError(({ error }: HttpErrorResponse) => {
              this.patchState({ isLoading: false, otpToken: '' });
              if (error.errors.includes('suspended')) {
                const suspendedTime = this.utilService.parseSuspendedTime(error.errors);
                if (suspendedTime) {
                  const remainingMinutes = this.utilService.getRemainingMinutes(suspendedTime);
                  const errorMessage = `You have attempted to send the code 3 times,<br> please try again in ${remainingMinutes} minutes.`;
                  this.patchState({
                    errorMessage: errorMessage,
                  });
                  this.showSuspendedToast(errorMessage);
                }
                this.patchState({ isSuspendAccount: true });
              } else {
                this.patchState({ errorMessage: error.errors });
              }
              return EMPTY;
            })
          )
      )
    )
  );

  showSuspendedToast(message: string) {
    this.messageService.add({
      key: 'message',
      severity: 'error',
      data: {
        title: '',
        body: message,
      },
    });
  }

  showBlockedDialog() {
    this.dialogComponentService
      .openConfirmationDialog({
        width: '400px',
        height: '420px',
        closable: true,
        body: {
          title: 'Account Blocked',
          message: `You've entered too many incorrect codes. Please contact helpdesk for assistance.`,
          closeText: 'Close',
          confirmText: 'Contact Helpdesk',
          isDoNothing: true,
          illustration: 'assets/icon/pin-unsuccessfull.svg',
        },
      })
      .onClose.pipe(
        tap(() => {
          this.router.navigateByUrl('/login').then(() => {
            window.location.reload();
          });
        })
      )
      .subscribe();
  }

  getEmail() {
    return this.authService.getEmail();
  }

  setResendDelaySeconds(seconds: number) {
    this.patchState({ resendDelaySeconds: seconds });
  }

  startCountdown() {
    const resendDelaySeconds = this.get().resendDelaySeconds || 30;

    // Stop any existing countdown
    this.stopCountdown();

    this.patchState({ countdown: resendDelaySeconds, isCountdownActive: true });
    this.saveOtpSession(); // Save initial session state

    this.countdownSubscription = interval(1000)
      .pipe(
        takeWhile(() => this.get().countdown > 0),
        tap(() => {
          this.patchState(state => ({
            countdown: state.countdown - 1,
          }));
          this.saveOtpSession(); // Update session data
        }),
        tap(() => {
          if (this.get().countdown === 0) {
            this.patchState({ isCountdownActive: false });
            this.clearOtpSession();
          }
        })
      )
      .subscribe();
  }

  private stopCountdown() {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = undefined;
    }
  }

  checkAccessToken() {
    if (this.authService.getAccessToken()) {
      this.router.navigateByUrl('/');
    }
  }

  resetState() {
    this.stopCountdown();
    this.clearOtpSession();
    this.patchState({
      errorMessage: '',
      isSuspendAccount: false,
      isAccountBlocked: false,
    });
  }

  saveOtpSession() {
    const currentState = this.get();
    const sessionData = {
      countdown: currentState.countdown,
      isCountdownActive: currentState.isCountdownActive,
      resendDelaySeconds: currentState.resendDelaySeconds,
      otpToken: currentState.otpToken,
      email: this.authService.getEmail(),
      timestamp: Date.now(),
    };

    localStorage.setItem('otpSession', JSON.stringify(sessionData));
  }

  loadOtpSession() {
    const sessionDataStr = localStorage.getItem('otpSession');
    if (sessionDataStr) {
      const sessionData = JSON.parse(sessionDataStr);
      const currentTime = Date.now();
      const timeDiff = Math.floor((currentTime - sessionData.timestamp) / 1000);
      const remainingCountdown = Math.max(0, sessionData.countdown - timeDiff);

      if (remainingCountdown > 0 && sessionData.otpToken) {
        this.authService.setTempToken(sessionData.otpToken);
        this.authService.setEmail(sessionData.email);
        this.patchState({
          countdown: remainingCountdown,
          isCountdownActive: remainingCountdown > 0,
          resendDelaySeconds: sessionData.resendDelaySeconds,
          otpToken: sessionData.otpToken,
        });

        // Continue countdown if still active
        if (remainingCountdown > 0) {
          this.continueCountdown();
        }
        return true;
      } else {
        this.clearOtpSession();
        return false;
      }
    }
    return false;
  }

  clearOtpSession() {
    localStorage.removeItem('otpSession');
  }

  setInitialSession(token: string, resendDelaySeconds: number) {
    this.patchState({
      countdown: resendDelaySeconds,
      resendDelaySeconds: resendDelaySeconds,
      otpToken: token,
      isCountdownActive: false, // Not active yet until dialog opens
    });
  }

  hasActiveSession(): boolean {
    const sessionDataStr = localStorage.getItem('otpSession');

    if (sessionDataStr) {
      const sessionData = JSON.parse(sessionDataStr);
      const currentTime = Date.now();
      const timeDiff = Math.floor((currentTime - sessionData.timestamp) / 1000);
      const remainingCountdown = Math.max(0, sessionData.countdown - timeDiff);

      return remainingCountdown > 0 && !!sessionData.otpToken;
    }

    return false;
  }

  private continueCountdown() {
    // Stop any existing countdown
    this.stopCountdown();

    this.countdownSubscription = interval(1000)
      .pipe(
        takeWhile(() => this.get().countdown > 0),
        tap(() => {
          this.patchState(state => ({
            countdown: state.countdown - 1,
          }));
          this.saveOtpSession(); // Update session data
        }),
        tap(() => {
          if (this.get().countdown === 0) {
            this.patchState({ isCountdownActive: false });
            this.clearOtpSession();
          }
        })
      )
      .subscribe();
  }

  override ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    super.ngOnDestroy();
  }
}
